import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import Pagination from '@/components/base/Pagination';
import SortableTh from '@/components/base/SortableTh';
import UserFormModal from './components/UserFormModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import { listUsers, deleteUser } from '@/lib/userService';
import { useTableControls } from '@/hooks/useTableControls';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/ToastProvider';
import { logAudit } from '@/lib/audit';
import { formatDateTime } from '@/lib/format';
import type { UserAccount, UserRole, UserStatus } from '@/types';

type RoleFilter = 'TODOS' | UserRole;
type StatusFilter = 'TODOS' | UserStatus;

export default function UsersPage() {
  const { isSuperUsuario, profileLoading, profile, user } = useAuth();
  const { notify } = useToast();

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('TODOS');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('TODOS');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [passwordUser, setPasswordUser] = useState<UserAccount | null>(null);
  const [toDelete, setToDelete] = useState<UserAccount | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: serviceError } = await listUsers();
    if (serviceError || !data) {
      setUsers([]);
      setError(serviceError ?? 'No fue posible cargar los usuarios. Intente nuevamente.');
    } else {
      setUsers(data.users);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isSuperUsuario) load();
    else setLoading(false);
  }, [isSuperUsuario, load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((item) => {
      const matchRole = roleFilter === 'TODOS' || item.role === roleFilter;
      const matchStatus = statusFilter === 'TODOS' || item.status === statusFilter;
      const matchTerm =
        !term ||
        (item.full_name || '').toLowerCase().includes(term) ||
        item.username.toLowerCase().includes(term);
      return matchRole && matchStatus && matchTerm;
    });
  }, [users, search, roleFilter, statusFilter]);

  const getSortValue = useCallback((item: UserAccount, key: string): string | number => {
    switch (key) {
      case 'full_name':
        return (item.full_name || '').toLowerCase();
      case 'username':
        return item.username.toLowerCase();
      case 'role':
        return item.role;
      case 'status':
        return item.status;
      case 'created_at':
        return new Date(item.created_at).getTime();
      case 'last_sign_in_at':
        return item.last_sign_in_at ? new Date(item.last_sign_in_at).getTime() : 0;
      default:
        return '';
    }
  }, []);

  const table = useTableControls(filtered, getSortValue, {
    pageSize: 10,
    initialKey: 'created_at',
    initialDirection: 'asc',
  });

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    const { error: serviceError } = await deleteUser(toDelete.id);
    setDeleting(false);

    if (serviceError) {
      notify({ tone: 'error', title: 'No se pudo eliminar', message: serviceError });
      return;
    }

    await logAudit({
      userId: profile?.id ?? null,
      userName: profile?.full_name || profile?.username || 'Usuario',
      action: 'ELIMINAR_USUARIO',
      module: 'Usuarios',
      recordId: toDelete.id,
      description: `Usuario eliminado: ${toDelete.username}.`,
    });

    notify({ tone: 'success', title: 'Usuario eliminado', message: `${toDelete.username} fue eliminado.` });
    setToDelete(null);
    load();
  };

  if (profileLoading) {
    return <Loading label="Verificando permisos..." />;
  }

  if (!isSuperUsuario) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Usuarios del sistema</h2>
          <p className="text-sm text-foreground-500">
            {filtered.length} de {users.length} usuarios
          </p>
        </div>
        <Button
          icon="ri-user-add-line"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          Nuevo usuario
        </Button>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre o usuario..."
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value as RoleFilter)}
            className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="TODOS">Todos los roles</option>
            <option value="superUsuario">Super usuario</option>
            <option value="Usuario">Usuario</option>
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="TODOS">Todos los estados</option>
            <option value="ACTIVO">Activo</option>
            <option value="INACTIVO">Inactivo</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <Loading label="Cargando usuarios..." />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-group-line"
          title={users.length === 0 ? 'No hay usuarios registrados' : 'Sin resultados'}
          description={
            users.length === 0
              ? 'Cree el primer usuario para dar acceso al sistema.'
              : 'Ajuste la búsqueda o los filtros aplicados.'
          }
          action={
            users.length === 0 ? (
              <Button
                icon="ri-user-add-line"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Nuevo usuario
              </Button>
            ) : (
              <Button
                variant="outline"
                icon="ri-refresh-line"
                onClick={() => {
                  setSearch('');
                  setRoleFilter('TODOS');
                  setStatusFilter('TODOS');
                }}
              >
                Limpiar filtros
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70">
                  <SortableTh
                    label="Nombre completo"
                    sortKey="full_name"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <SortableTh
                    label="Usuario"
                    sortKey="username"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <SortableTh
                    label="Rol"
                    sortKey="role"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <SortableTh
                    label="Estado"
                    sortKey="status"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <SortableTh
                    label="Creación"
                    sortKey="created_at"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <SortableTh
                    label="Último acceso"
                    sortKey="last_sign_in_at"
                    activeKey={table.sortKey}
                    direction={table.direction}
                    onSort={table.toggleSort}
                  />
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((item) => {
                  const isSelf = item.id === user?.id;
                  const active = item.status === 'ACTIVO';
                  return (
                    <tr
                      key={item.id}
                      className="border-b border-background-200 last:border-0 transition-colors hover:bg-background-100/50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                            <i className="ri-user-line"></i>
                          </span>
                          <div className="min-w-0">
                            <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground-900">
                              {item.full_name || '—'}
                              {item.is_primary_admin && (
                                <i className="ri-shield-star-line text-sm text-amber-500" title="Administrador principal"></i>
                              )}
                              {isSelf && (
                                <span className="rounded-full bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-800">
                                  Tú
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">{item.username}</td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={item.role === 'superUsuario' ? 'primary' : 'neutral'}
                          icon={item.role === 'superUsuario' ? 'ri-shield-star-line' : 'ri-user-line'}
                        >
                          {item.role === 'superUsuario' ? 'Super usuario' : 'Usuario'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={active ? 'success' : 'neutral'}
                          icon={active ? 'ri-checkbox-circle-line' : 'ri-forbid-2-line'}
                        >
                          {active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">
                        {item.last_sign_in_at ? formatDateTime(item.last_sign_in_at) : 'Sin registro'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(item);
                              setFormOpen(true);
                            }}
                            aria-label={`Editar ${item.username}`}
                            title="Editar"
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPasswordUser(item)}
                            aria-label={`Cambiar contraseña de ${item.username}`}
                            title="Cambiar contraseña"
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                          >
                            <i className="ri-lock-password-line text-base"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setToDelete(item)}
                            disabled={isSelf || item.is_primary_admin}
                            aria-label={`Eliminar ${item.username}`}
                            title={
                              isSelf
                                ? 'No puede eliminar su propio usuario'
                                : item.is_primary_admin
                                  ? 'No se puede eliminar el administrador principal'
                                  : 'Eliminar'
                            }
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-200 disabled:hover:bg-transparent"
                          >
                            <i className="ri-delete-bin-6-line text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={table.page}
            pageCount={table.pageCount}
            total={table.total}
            pageSize={table.pageSize}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        </div>
      )}

      <UserFormModal
        open={formOpen}
        user={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          setFormOpen(false);
          setEditing(null);
          load();
        }}
      />

      <ChangePasswordModal user={passwordUser} onClose={() => setPasswordUser(null)} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="¿Está seguro de que desea eliminar este usuario?"
        description="Esta acción puede afectar los registros asociados."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        tone="danger"
        icon="ri-delete-bin-6-line"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}